import React from "react";
import { Col } from "react-bootstrap";

import { Constants } from "../../../assets/utils/lib/constants";
import Control from "../../../assets/vfx/controls/control";
import { DropdownOption } from "../../../assets/vfx/controls/components/dropdown/controlDropdown";
import { GRID_ACTION_TYPES } from "../../../assets/types";
import {
  IControlStyles,
  PageContent,
  useControlStyles,
} from "../../../assets/layouts";
import Form from "../../../assets/vfx/form/form";

import { SearchContainer } from "../../../assets/vfx/search/searchContainer";
import { GridStateDto, SearchStateDto } from "../../../services/dto";
import { StateService } from "./stateService";
import { ControlDataGridColumnProps } from "../../../assets/vfx/controls/components/datagrid/controlDataGrid";
import {
  createActionColumn,
  createLayoutOptions,
} from "../../../assets/configuration/gridConfig";

interface StatePageProps {
  controlStyles?: IControlStyles | undefined;
  onCustomEvent?: (actionType: string, dataId: string) => void;
  regionLookupValues?: DropdownOption[] | undefined;
}

const defaultSearchQuery: SearchStateDto = {
  name: Constants.emptyString,
  regionId: undefined,
  pageNumber: 1,
  pageSize: 10,  
};

const DATA_ID_FIELD = "stateID";

const gridColumns: ControlDataGridColumnProps[] = [
  {
    data: DATA_ID_FIELD,
    title: "ID",
    visible: false,    
    columnType: "number",
    alignment: "right",
    sortable: false,
  },
  {
    data: "stateName",
    title: "State",
    visible: true,
    columnType: "text",
    alignment: "left",    
    width: "18%",
  },
  {
    data: "regionName",
    title: "Region",
    visible: true,
    columnType: "text",
    alignment: "left",
    width: "24%",
  },
];

export const StatePage = (props: StatePageProps) => {
  const styles: IControlStyles = props.controlStyles ?? useControlStyles();
  const [searchQuery, setSearchQuery] =
    React.useState<SearchStateDto>(defaultSearchQuery);

  const stateGridQuery = StateService.gridQuery(searchQuery);
  const { data } = stateGridQuery;
  const isGridLoading = stateGridQuery.isLoading || stateGridQuery.isFetching;
  const states: GridStateDto[] = data?.result ?? [];
  const hasData: boolean = states.length > 0;
  const actionColumn = createActionColumn(styles, DATA_ID_FIELD, [
    GRID_ACTION_TYPES.EDIT,
    GRID_ACTION_TYPES.VIEW,
  ]);
  const layoutOptions = createLayoutOptions(hasData);

  const handleSearch = (values: any): void => {
    const updatedSearchQuery: SearchStateDto = { ...defaultSearchQuery };
    if (values.name) updatedSearchQuery.name = values.name;
    if (values.regionId) updatedSearchQuery.regionId = values.regionId;
    setSearchQuery(updatedSearchQuery);
  };

  const handleClear = (): void => {
    setSearchQuery(prev => ({
          ...defaultSearchQuery,
          sortBy: prev.sortBy,
          pageSize: prev.pageSize ?? 10,
      }));
  };

  const handleNew = (): void | undefined =>
    props.onCustomEvent?.(GRID_ACTION_TYPES.NEW, Constants.emptyGuid);

  return (
    <PageContent title="States">
      <SearchContainer
        initialValues={{
          name: Constants.emptyString,
          regionId: Constants.emptyString,
        }}
        onSearch={handleSearch}
        onNew={handleNew}
        onClear={handleClear}
        enableReinitialize
      >
        <Col lg={4}>
          <Form.Text name={"name"} label={"Name"} />
        </Col>
        <Col lg={4} className="ms-2">
          <Form.Dropdown
            name="regionId"
            label="Region"
            blankOption={{
              key: Constants.emptyGuid,
              display: "Select Region",
              value: Constants.emptyString,
            }}
            options={props.regionLookupValues}
          />
        </Col>
      </SearchContainer>

      <Control.DataGrid
        dataId={DATA_ID_FIELD}
        cols={gridColumns}
        actionCol={actionColumn}
        layoutOptions={layoutOptions}
        onCustomEvent={props.onCustomEvent}
        data={states}
        totalRecords={data?.totalSize ?? (data?.result?.length ?? 0)}
        query={searchQuery}
        loading={isGridLoading}
        onQueryChange={(patch) => setSearchQuery(query => ({ ...query, ...patch }))}
      />
    </PageContent>
  );
};
